import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { X, Plus, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const AddCandidatureManagerHSModal = ({ isOpen, onClose, onSuccess, offerId }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    commentaire: ''
  });
  const [files, setFiles] = useState({
    cv: null,
    lettre: null
  });

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (type, file) => {
    if (!file) {
      setFiles(prev => ({
        ...prev,
        [type]: null
      }));
      return;
    }

    // Validation du format de fichier
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format de fichier non autorisé. Seuls les fichiers PDF, DOC et DOCX sont acceptés.');
      return;
    }

    // Validation de la taille (10 Mo max)
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      toast.error('Le fichier est trop volumineux. Taille maximum autorisée : 10 Mo.');
      return;
    }

    setFiles(prev => ({
      ...prev,
      [type]: file
    }));
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.ok) {
      const result = await response.json();
      return result.filename;
    } else {
      throw new Error('Erreur lors de l\'upload du fichier');
    }
  };

  const handleSubmit = async () => {
    // Validation des champs obligatoires
    if (!formData.prenom.trim()) {
      toast.error('Le prénom est requis');
      return;
    }
    if (!formData.nom.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('L\'email est requis');
      return;
    }
    if (!formData.telephone.trim()) {
      toast.error('Le numéro de téléphone est requis');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      let cvFilename = null;
      let lettreFilename = null;

      // Upload des fichiers si présents
      if (files.cv) {
        cvFilename = await uploadFile(files.cv);
      }
      if (files.lettre) {
        lettreFilename = await uploadFile(files.lettre);
      }

      // Créer la candidature
      const candidatureData = {
        job_offer_id: offerId,
        prenom: formData.prenom.trim(),
        nom: formData.nom.trim(),
        email: formData.email.trim(),
        telephone: formData.telephone.trim(),
        cv_filename: cvFilename,
        lettre_filename: lettreFilename,
        commentaire: formData.commentaire.trim(),
        ajouter_au_vivier: false // Pas de case à cocher pour Manager HS
      };

      const response = await fetch(`${API}/candidatures`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(candidatureData),
      });

      if (response.ok) {
        toast.success('Candidature ajoutée avec succès');
        // Reset du formulaire
        setFormData({
          prenom: '',
          nom: '',
          email: '',
          telephone: '',
          commentaire: ''
        });
        setFiles({
          cv: null,
          lettre: null
        });
        onSuccess();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de l\'ajout de la candidature');
      }
    } catch (error) {
      console.error('Error adding candidature:', error);
      toast.error('Erreur lors de l\'ajout de la candidature');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset du formulaire
    setFormData({
      prenom: '',
      nom: '',
      email: '',
      telephone: '',
      commentaire: ''
    });
    setFiles({
      cv: null,
      lettre: null
    });
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <Card className="modal-card">
          <CardHeader className="modal-header">
            <div className="flex justify-between items-center">
              <CardTitle className="modal-title">
                <Plus size={20} className="mr-2" />
                Nouvelle candidature - Horizon Solution
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="modal-close"
              >
                <X size={20} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="modal-content">
            <div className="form-container">
              {/* Prénom et Nom */}
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="prenom" className="form-label">
                    Prénom *
                  </Label>
                  <Input
                    id="prenom"
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => handleInputChange('prenom', e.target.value)}
                    className="form-input"
                    placeholder="Prénom du candidat"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="nom" className="form-label">
                    Nom *
                  </Label>
                  <Input
                    id="nom"
                    type="text"
                    value={formData.nom}
                    onChange={(e) => handleInputChange('nom', e.target.value)}
                    className="form-input"
                    placeholder="Nom du candidat"
                  />
                </div>
              </div>

              {/* Email et Téléphone */}
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="email" className="form-label">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="form-input"
                    placeholder="Email du candidat"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="telephone" className="form-label">
                    Téléphone *
                  </Label>
                  <Input
                    id="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => handleInputChange('telephone', e.target.value)}
                    className="form-input"
                    placeholder="Numéro de téléphone"
                  />
                </div>
              </div>

              {/* CV Upload */}
              <div className="form-group">
                <Label htmlFor="cv" className="form-label">
                  CV
                </Label>
                <div className="file-upload-container">
                  <Input
                    id="cv"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange('cv', e.target.files[0])}
                    className="file-input"
                  />
                  {files.cv && (
                    <div className="file-info">
                      <Upload size={16} className="mr-2" />
                      <span className="file-name">{files.cv.name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Lettre de motivation Upload */}
              <div className="form-group">
                <Label htmlFor="lettre" className="form-label">
                  Lettre de motivation
                </Label>
                <div className="file-upload-container">
                  <Input
                    id="lettre"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange('lettre', e.target.files[0])}
                    className="file-input"
                  />
                  {files.lettre && (
                    <div className="file-info">
                      <Upload size={16} className="mr-2" />
                      <span className="file-name">{files.lettre.name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Commentaire */}
              <div className="form-group">
                <Label htmlFor="commentaire" className="form-label">
                  Commentaire
                </Label>
                <Textarea
                  id="commentaire"
                  value={formData.commentaire}
                  onChange={(e) => handleInputChange('commentaire', e.target.value)}
                  className="form-textarea"
                  placeholder="Commentaires sur la candidature..."
                  rows={3}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="modal-actions">
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="btn-primary"
                style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
              >
                {loading ? 'Envoi...' : 'Envoyer'}
              </Button>
              <Button
                onClick={handleCancel}
                disabled={loading}
                variant="outline"
                className="btn-cancel"
              >
                Annuler
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddCandidatureManagerHSModal;